import { useCallback, useContext, useMemo, useState } from 'react';
import useLegacySavedFilter from './useLegacySavedFilter';
import aggregateMetricMatrix from '../api/aggregateMetricMatrix';
import toFilterInput from '../toFilterInput';
import mergeFilterInputs from '../mergeFilterInputs';
import { toFilterPlate } from '../migrateScope';
import useMetric from './useMetric';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import GqlClientContext from '../contexts/GqlClientContext';
import useDateScope from './useDateScope';
import DateInputContext from '../contexts/DateInputContext';
import VariableFiltersContext from '../contexts/VariableFiltersContext';
import EntityDetailsContext from '../screens/EntityDetailsShow/EntityDetailsContext';
import useLockedDebouncedEffect from './useLockedDebouncedEffect';
import useMetricAggregationRequest from './useMetricAggregationRequest';
import useToFixedFilters from 'hooks/useToFixedFilters';

interface GetMetricListItemArgs {
  client: ApolloClient<NormalizedCacheObject>;
  metrics: (MetricInput | Metrics.SpecialMetric)[];
  expressions: Expression2[];
  filterInput: FilterInput;
  currentDateScope: DateRangeInput;
  previousDateScope?: DateRangeInput;
  popupReportFilter: UnSavedFilter; // For the popup report filter
}

const createPopupReportFilter = ({
  savedFilter,
  comparison,
  dateField,
  variableDrillDowns,
  dateScope,
  dateRange,
  relativeDateRange,
  advancedRelativeDateRange,
  toDrillDowns,
  entityFilterPlates,
  isEntityFilterEnabled,
}: {
  savedFilter?: LegacySavedFilter;
  comparison?: PersistedComparisonType;
  dateField: string;
  variableDrillDowns: VariableDrillDownType[];
  dateScope: DateRangeInput;
  dateRange?: DateRangeInput;
  relativeDateRange?: RelativeDateRange;
  advancedRelativeDateRange?: AdvancedRelativeDateRange;
  toDrillDowns: ({
    plates,
    variableDrillDowns,
  }: {
    plates?: FilterPlateType[] | undefined;
    variableDrillDowns?: VariableDrillDownType[] | undefined;
  }) => FixedFilter[];
  entityFilterPlates?: FilterPlateType[];
  isEntityFilterEnabled?: boolean;
}): UnSavedFilter => {
  const savedFilterPlates = savedFilter ? savedFilter.scope : window.emptyArray;
  const scorePlates = [
    ...savedFilterPlates,
    ...(isEntityFilterEnabled && entityFilterPlates
      ? entityFilterPlates
      : window.emptyArray),
  ];

  return {
    drillDowns: toDrillDowns({
      plates: savedFilter ? savedFilter.drillDowns : window.emptyArray,
      variableDrillDowns,
    }).map(toFilterPlate),
    mustExistFilters: savedFilter ? savedFilter.mustExistFilters : undefined,
    mustNotExistFilters: savedFilter
      ? savedFilter.mustNotExistFilters
      : undefined,
    comparison,
    scope: toDrillDowns({
      plates: scorePlates,
      variableDrillDowns,
    }).map(toFilterPlate),
    dateScope,
    dateField,
    dateRange,
    relativeDateRange,
    advancedRelativeDateRange,
  };
};

const useMetricListItemArgs = ({
  item,
  comparison,
}: {
  item?: MetricListItemType;
  comparison?: PersistedComparisonType;
}) => {
  // Context
  const { client } = useContext(GqlClientContext);
  const { resolveMetricForRequest } = useMetricAggregationRequest();
  const { variableFilters } = useContext(VariableFiltersContext);
  const entityDetailsContext = useContext(EntityDetailsContext);
  const { dateField, dateRange, relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);

  // Hooks
  const { savedFilter } = useLegacySavedFilter(
    item ? item.savedFilterId : undefined,
  );
  const metric = useMetric(item ? item.metricId : undefined);
  const currentDateScope = useDateScope({ isMetricListItem: true });
  const previousDateScope = useDateScope({
    comparison,
    isMetricListItem: true,
  });
  const toDrillDowns = useToFixedFilters();

  // The GQL metric/expression pieces for this item's metric. `ready` is false
  // while a compound metric's inputs are still loading — in that state we must
  // not fire (the expression would reference an input missing from `metrics`).
  const resolved = useMemo(
    () =>
      metric
        ? resolveMetricForRequest(metric, {
            isDefaultFiltersDisabled: !!(item && item.isDefaultFiltersDisabled),
          })
        : undefined,
    [metric, item, resolveMetricForRequest],
  );

  // Callbacks
  const getFilterInput = useCallback((): FilterInput => {
    if (!savedFilter || !item) {
      return {};
    }

    const baseFilter = toFilterInput(
      savedFilter,
      variableFilters,
      toDrillDowns,
    );

    if (entityDetailsContext && item.isEntityFilterEnabled) {
      return mergeFilterInputs(baseFilter, entityDetailsContext.entityFilter);
    }

    return baseFilter;
  }, [entityDetailsContext, item, savedFilter, toDrillDowns, variableFilters]);

  const args = useMemo((): GetMetricListItemArgs | undefined => {
    if (!metric || !savedFilter || !resolved || !resolved.ready) {
      return undefined;
    }

    const popupReportFilter = createPopupReportFilter({
      savedFilter,
      dateScope: currentDateScope,
      comparison,
      dateField,
      variableDrillDowns: variableFilters,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
      toDrillDowns,
      entityFilterPlates: entityDetailsContext
        ? entityDetailsContext.entityFilterAsFilterPlates
        : window.emptyArray,
      isEntityFilterEnabled: item ? item.isEntityFilterEnabled : false,
    });

    return {
      client,
      metrics: [
        ...resolved.metricInputs,
        ...(resolved.special ? [resolved.special] : []),
      ],
      expressions: resolved.expression ? [resolved.expression] : [],
      filterInput: getFilterInput(),
      currentDateScope,
      previousDateScope: comparison ? previousDateScope : undefined,
      popupReportFilter,
    };
  }, [
    advancedRelativeDateRange,
    client,
    comparison,
    currentDateScope,
    dateField,
    dateRange,
    entityDetailsContext,
    getFilterInput,
    item,
    metric,
    previousDateScope,
    relativeDateRange,
    resolved,
    savedFilter,
    toDrillDowns,
    variableFilters,
  ]);

  return args;
};

const useMetricListItem = (
  item?: MetricListItemType,
  isBonusPeriodMode?: boolean,
  comparison?: PersistedComparisonType,
) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [current, setCurrent] = useState<number | null | undefined>();
  const [previous, setPrevious] = useState<number | null | undefined>();
  const [popupReportFilter, setPopupReportFilter] = useState<
    UnSavedFilter | undefined
  >();
  const args = useMetricListItemArgs({ item, comparison });
  const handleResponse = useCallback(
    ({
      currentResponse,
      previousResponse,
      popupReportFilter,
    }: {
      currentResponse: MetricsResponse;
      previousResponse: MetricsResponse | undefined;
      popupReportFilter: UnSavedFilter;
    }) => {
      if (!item) {
        return;
      }
      setIsLoading(false);
      setCurrent(currentResponse[0][item.metricId]);
      setPrevious(
        previousResponse ? previousResponse[0][item.metricId] : undefined,
      );
      setPopupReportFilter(popupReportFilter);
    },
    [item],
  );

  const getMetricResponses = useCallback(
    async ({
      client,
      metrics,
      expressions,
      filterInput,
      currentDateScope,
      previousDateScope,
      popupReportFilter,
    }: GetMetricListItemArgs) => {
      setIsLoading(true);
      const currentMetricRequest = aggregateMetricMatrix({
        client,
        metrics,
        expressions,
        filterInput: [filterInput],
        dateScope: currentDateScope,
      });
      const previousMetricRequest = previousDateScope
        ? aggregateMetricMatrix({
            client,
            metrics,
            expressions,
            filterInput: [filterInput],
            dateScope: previousDateScope,
          })
        : Promise.resolve(undefined);

      const [currentResponse, previousResponse] = await Promise.all([
        currentMetricRequest,
        previousMetricRequest,
      ]);

      return {
        currentResponse,
        previousResponse,
        popupReportFilter,
      };
    },
    [],
  );

  useLockedDebouncedEffect({
    args,
    responseHandler: handleResponse,
    callback: getMetricResponses,
    // A gadget's read: try a failed request once more
    shouldRetryOnce: true,
  });

  return {
    isLoading,
    current,
    currentFilterInput: args ? args.filterInput : undefined,
    previous,
    previousFilterInput: args ? args.filterInput : undefined,
    popupReportFilter,
    currentDateRange: args ? args.currentDateScope : undefined,
    previousDateRange: args ? args.previousDateScope : undefined,
  };
};

export default useMetricListItem;
